import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ErrorApi, pedir } from "../api/cliente.ts";
import type {
  Abono,
  Accion,
  RazonRevision,
  Cambios,
  Campos,
  Configuracion,
  CorreoEnRevision,
  CorreosSinLeer,
  Equipo,
  Invitacion,
  Libro,
  FacturaCartera,
  Credencial,
  CredencialCreada,
  EnlaceTelegram,
  EstadoTelegram,
  Metricas,
  Recibidos,
  ResultadoCorreo,
  ResultadoConfig,
  Rol,
  CargasRechazadas,
  Integraciones,
} from "../api/tipos.ts";

/**
 * Lectura del libro. Es la consulta central del panel: casi todas las pantallas
 * se derivan de ella, así que se pide una vez y se comparte.
 *
 * El libro se recalcula entero en cada corrida del motor, de modo que no hay
 * nada que actualizar de forma optimista: tras decidir se vuelve a pedir, y lo
 * que llega es la verdad.
 */
export const LLAVE_LIBRO = ["libro"] as const;

export function usarLibro(vivo = false) {
  return useQuery({
    queryKey: LLAVE_LIBRO,
    queryFn: ({ signal }) => pedir<Libro>("/api/v1/libro", { senal: signal }),
    // Los pagos entran por correo en cualquier momento: media hora de caché
    // convertiría el panel en una foto vieja.
    staleTime: 15_000,
    // `vivo`: la caja, donde un cliente espera delante a que aparezca su pago
    // (SPEC-029 §12). Cada 10 s sin tocar nada.
    refetchInterval: vivo ? 10_000 : 60_000,
    refetchOnWindowFocus: true,
    retry: (intentos, error) => !(error instanceof ErrorApi) && intentos < 2,
  });
}

export function usarRevision() {
  return useQuery({
    queryKey: ["revision"],
    queryFn: ({ signal }) =>
      pedir<{ correos: CorreoEnRevision[] }>("/api/v1/revision", { senal: signal }),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Todo lo que llegó al buzón, con su porqué (SPEC-021).
 *
 * Sustituye a `usarRevision` en la pantalla: "en revisión" es un filtro de esto.
 */
export function usarRecibidos(estado: ResultadoCorreo | "" = "", archivados = false, vivo = false, activo = true) {
  return useQuery({
    // `activo`: el menú pregunta si el alta está completa, y solo para quien configura.
    enabled: activo,
    // `vivo`: mientras alguien espera su primer correo de prueba (SPEC-029 §15).
    refetchInterval: vivo ? 10_000 : false,
    queryKey: ["recibidos", estado, archivados],
    queryFn: ({ signal }) => {
      const q = new URLSearchParams();
      if (estado) q.set("estado", estado);
      if (archivados) q.set("archivados", "1");
      const cola = q.toString();
      return pedir<Recibidos>(`/api/v1/recibidos${cola ? `?${cola}` : ""}`, { senal: signal });
    },
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Cuántos correos del banco que parecen pagos no se pudieron leer (SPEC-031).
 * Es lo que avisa que el banco cambió su redacción: sin esto los pagos dejan de
 * entrar y nada lo dice. `activo` en falso no pregunta: el cajero no ve Recibidos.
 */
export function usarCorreosSinLeer(activo = true) {
  return useQuery({
    enabled: activo,
    queryKey: ["correos-sin-leer"],
    queryFn: ({ signal }) => pedir<CorreosSinLeer>("/api/v1/correos/sin-leer", { senal: signal }),
    // Un pago sin cargar no puede esperar media hora a que alguien recargue.
    staleTime: 30_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
}

/**
 * Lo que el sistema del cliente mandó y se rechazó (SPEC-019 §8 bis).
 *
 * `staleTime` corto: quien abre esta pantalla suele estar depurando una
 * integración con el proveedor al teléfono, y una lista de hace cinco minutos
 * le haría creer que su último intento tampoco llegó.
 */
/**
 * Qué hay conectado y cómo va (SPEC-026).
 *
 * `staleTime` corto porque es una pantalla que se mira justo cuando se sospecha
 * que algo dejó de funcionar, y ahí un dato de hace cinco minutos contesta la
 * pregunta anterior.
 */
export function usarIntegraciones() {
  return useQuery({
    queryKey: ["integraciones"],
    queryFn: ({ signal }) => pedir<Integraciones>("/api/v1/integraciones", { senal: signal }),
    staleTime: 10_000,
    retry: false,
  });
}

export function usarCargasRechazadas() {
  return useQuery({
    queryKey: ["cargas-rechazadas"],
    queryFn: ({ signal }) => pedir<CargasRechazadas>("/api/v1/cargas-rechazadas", { senal: signal }),
    staleTime: 10_000,
    retry: false,
  });
}

/**
 * Saca de la bandeja un correo que no es un pago, o lo devuelve (SPEC-024).
 *
 * Un correo a la vez. La API rechaza cualquier forma de lote a propósito —una
 * regla que archivara por parecido sería un filtro silencioso— y este gancho no
 * la esquiva envolviéndola en un bucle.
 */
export function usarArchivarRecibido() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archivar = true }: { id: number; archivar?: boolean }) =>
      pedir<{ archivado: boolean }>("/api/v1/recibidos/archivar", {
        metodo: "POST",
        cuerpo: { id, archivar },
      }),
    // También el libro: al desarchivar, `reprocesar` puede haber creado el
    // movimiento que faltaba.
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ["recibidos"] });
      cache.invalidateQueries({ queryKey: LLAVE_LIBRO });
    },
  });
}

const LLAVE_TELEGRAM = ["telegram"] as const;

/** Si hay Telegram conectado, y si este servidor puede ofrecerlo (SPEC-023). */
export function usarTelegram(activo = true) {
  return useQuery({
    enabled: activo,
    queryKey: LLAVE_TELEGRAM,
    queryFn: ({ signal }) => pedir<EstadoTelegram>("/api/v1/telegram", { senal: signal }),
    staleTime: 15_000,
    retry: false,
  });
}

/**
 * Pide el enlace de vinculación.
 *
 * No invalida nada al terminar: el vínculo no existe todavía — lo crea Telegram
 * cuando el cliente añade el bot. Quien vuelve a preguntar es la pantalla,
 * mientras la ventana está abierta.
 */
export function usarEnlaceTelegram() {
  return useMutation({
    mutationFn: (grupo: boolean) =>
      pedir<EnlaceTelegram>("/api/v1/telegram/vincular", { metodo: "POST", cuerpo: { grupo } }),
  });
}

export function usarDesvincularTelegram() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => pedir<{ ok: true }>("/api/v1/telegram/desvincular", { metodo: "POST", cuerpo: {} }),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: LLAVE_TELEGRAM });
      cache.invalidateQueries({ queryKey: ["configuracion"] });
    },
  });
}

const LLAVE_CREDENCIALES = ["credenciales"] as const;

/** Las credenciales de servicio de la cuenta (SPEC-019 §3). Sin secretos. */
export function usarCredenciales() {
  return useQuery({
    queryKey: LLAVE_CREDENCIALES,
    queryFn: ({ signal }) =>
      pedir<{ credenciales: Credencial[] }>("/api/v1/credenciales", { senal: signal }),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Crear devuelve el token UNA vez.
 *
 * No se guarda en la caché de consultas a propósito: ahí viviría más de lo
 * necesario y podría acabar en un volcado de estado. Lo sostiene la pantalla
 * mientras hace falta, y desaparece al cerrarla.
 */
export function usarCrearCredencial() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (nombre: string) =>
      pedir<CredencialCreada>("/api/v1/credenciales", { metodo: "POST", cuerpo: { nombre } }),
    onSuccess: () => cache.invalidateQueries({ queryKey: LLAVE_CREDENCIALES }),
  });
}

export function usarRevocarCredencial() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      pedir<{ ok: true }>("/api/v1/credenciales/revocar", { metodo: "POST", cuerpo: { id } }),
    onSuccess: () => cache.invalidateQueries({ queryKey: LLAVE_CREDENCIALES }),
  });
}

export function usarMetricas(dias = 30) {
  return useQuery({
    queryKey: ["metricas", dias],
    queryFn: ({ signal }) => pedir<Metricas>(`/api/v1/metricas?dias=${dias}`, { senal: signal }),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Los clientes para elegir al identificar un pago (SPEC-029 §5). Nombre y
 * documento, sin montos: lo pide decidir, no ver la cartera.
 */
export function usarClientes(activo = true) {
  return useQuery({
    queryKey: ["clientes"],
    queryFn: ({ signal }) => pedir<{ clientes: { nombre: string; nit: string }[] }>("/api/v1/clientes", { senal: signal }),
    enabled: activo,
    staleTime: 60_000,
  });
}

export interface Decision {
  clave: string;
  accion: Accion;
  cliente?: string;
  /** El documento: la identidad. El nombre de arriba es una copia legible. */
  clienteNit?: string;
  /** Contra qué se aplica. Va aparte del cliente y son varias (SPEC-028 §4). */
  facturas?: string[];
  /** Solo LIGAR: a qué pago registrado corresponde este del banco (SPEC-029 §13). */
  abonos?: string[];
  nota?: string;
}

/**
 * Decidir. Al terminar se invalida el libro entero, no solo la fila: una
 * confirmación mueve la cartera, los huérfanos y el resumen a la vez, y además
 * puede resolver otros pagos por identidad aprendida (SPEC-013).
 */
export function usarDecidir() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (d: Decision) =>
      pedir<{ ok: boolean; accion: Accion }>("/api/v1/decisiones", { metodo: "POST", cuerpo: d }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: LLAVE_LIBRO });
      void cache.invalidateQueries({ queryKey: ["metricas"] });
      // Un cliente nuevo con documento entra en la lista para la próxima vez.
      void cache.invalidateQueries({ queryKey: ["clientes"] });
    },
  });
}

/** «Ya lo revisé» sobre un faltante (SPEC-029 §13). */
export function usarRevisarAlerta() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (r: { clave: string; razon: RazonRevision; nota?: string }) =>
      pedir<{ ok: true }>("/api/v1/alertas/revisar", { metodo: "POST", cuerpo: r }),
    onSuccess: () => void cache.invalidateQueries({ queryKey: LLAVE_LIBRO }),
  });
}

/**
 * A qué cuenta entró uno o varios pagos (SPEC-030 §6). `cuenta` vacía devuelve
 * los pagos a lo que diga el banco.
 */
export function usarElegirCuenta() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (e: { claves: string[]; banco: string; cuenta: string }) =>
      pedir<{ ok: true; pagos: number }>("/api/v1/pagos/cuenta", { metodo: "POST", cuerpo: e }),
    onSuccess: () => void cache.invalidateQueries({ queryKey: LLAVE_LIBRO }),
  });
}

/** Varias decisiones de una vez: la mesa de conciliación (SPEC-029 §14). */
export function usarDecidirLote() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (decisiones: Decision[]) =>
      pedir<{ guardadas: number; resultados: { clave: string | null; ok: boolean; error?: string }[] }>(
        "/api/v1/decisiones/lote",
        { metodo: "POST", cuerpo: { decisiones } },
      ),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: LLAVE_LIBRO });
      void cache.invalidateQueries({ queryKey: ["metricas"] });
    },
  });
}

export function usarInvitar() {
  return useMutation({
    mutationFn: (entrada: { correo: string; rol: Rol; tipo?: "invitacion" | "restablecimiento" }) =>
      pedir<Invitacion>("/api/v1/invitaciones", { metodo: "POST", cuerpo: entrada }),
  });
}

export function usarCambiarContrasena() {
  return useMutation({
    mutationFn: (entrada: { actual: string; nueva: string }) =>
      pedir<{ ok: boolean }>("/api/v1/contrasena", { metodo: "POST", cuerpo: entrada }),
  });
}

export function usarAceptarInvitacion() {
  return useMutation({
    mutationFn: (entrada: { token: string; contrasena: string }) =>
      pedir<{ ok: boolean; rol: Rol }>("/api/v1/invitaciones/aceptar", {
        metodo: "POST",
        cuerpo: entrada,
        publica: true,
      }),
  });
}

// ─────────────────────────── configuración (SPEC-017) ───────────────────────────

export const LLAVE_CONFIG = ["configuracion"] as const;

export function usarConfiguracion(activo = true) {
  return useQuery({
    enabled: activo,
    queryKey: LLAVE_CONFIG,
    queryFn: ({ signal }) => pedir<Configuracion>("/api/v1/configuracion", { senal: signal }),
    // La configuración la cambia una persona, no el correo entrante: no hace
    // falta refrescarla sola.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Guardar. Al terminar se invalida el libro además de la configuración: cambiar
 * el umbral o encender una regla cambia lo que el motor decide, así que el libro
 * que hay en pantalla deja de ser el que produciría esta configuración.
 */
export function usarGuardarConfiguracion() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (cambios: Cambios) =>
      pedir<ResultadoConfig>("/api/v1/configuracion", { metodo: "PUT", cuerpo: cambios }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: LLAVE_CONFIG });
      void cache.invalidateQueries({ queryKey: LLAVE_LIBRO });
    },
  });
}

/** Valida y devuelve los avisos SIN escribir nada (SPEC-017 §5). */
export function usarSimularConfiguracion() {
  return useMutation({
    mutationFn: (cambios: Cambios) =>
      pedir<ResultadoConfig>("/api/v1/configuracion?simular=1", { metodo: "PUT", cuerpo: cambios }),
  });
}

// ─────────────────────────── equipo (SPEC-018) ───────────────────────────

export const LLAVE_EQUIPO = ["equipo"] as const;

export function usarEquipo() {
  return useQuery({
    queryKey: LLAVE_EQUIPO,
    queryFn: ({ signal }) => pedir<Equipo>("/api/v1/equipo", { senal: signal }),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Los tres cambios de equipo comparten gancho porque comparten consecuencia:
 * al terminar hay que releer el equipo, y si te cambiaste el rol a ti mismo,
 * también tu propia sesión — el rol sale de la base en cada petición, así que
 * la interfaz tiene que enterarse.
 */
function usarCambioDeEquipo<T>(ruta: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (entrada: T) => pedir<{ ok: boolean }>(ruta, { metodo: "POST", cuerpo: entrada }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: LLAVE_EQUIPO });
      void cache.invalidateQueries({ queryKey: LLAVE_LIBRO });
    },
  });
}

export const usarCambiarRol = () =>
  usarCambioDeEquipo<{ usuarioId: string; rol: Rol }>("/api/v1/equipo/rol");

export const usarQuitarDelEquipo = () =>
  usarCambioDeEquipo<{ usuarioId: string }>("/api/v1/equipo/quitar");

export const usarAnularInvitacion = () =>
  usarCambioDeEquipo<{ id: string }>("/api/v1/invitaciones/anular");

// ─────────────── cartera: abonos y facturas (SPEC-000 §2) ───────────────
//
// Toda escritura aquí invalida TAMBIÉN el libro. No es por prudencia: el libro
// es la conciliación recalculada sobre estos datos, así que registrar un abono
// cambia lo que el panel muestra en todas las demás pantallas. Sin esto, el
// contador guarda un pago y la bandeja sigue enseñándole el mismo pendiente.

export const LLAVE_ABONOS = ["abonos"] as const;
export const LLAVE_FACTURAS = ["facturas"] as const;
export const LLAVE_CAMPOS = ["campos"] as const;

/**
 * Qué campos pide cada entidad, según el backend.
 *
 * Se consulta en vez de escribirse aquí porque las reglas viven en el esquema
 * del servidor, que es el que valida. Duplicar la lista en el front garantiza
 * que un día pida un campo que ya no existe, o deje de pedir uno obligatorio y
 * el usuario descubra el problema al guardar.
 */
export function usarCampos() {
  return useQuery({
    queryKey: LLAVE_CAMPOS,
    queryFn: ({ signal }) => pedir<Campos>("/api/v1/campos", { senal: signal }),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function usarAbonos() {
  return useQuery({
    queryKey: LLAVE_ABONOS,
    queryFn: ({ signal }) => pedir<{ abonos: Abono[] }>("/api/v1/abonos", { senal: signal }),
    select: (d) => d.abonos,
    retry: false,
  });
}

export function usarFacturasCartera() {
  return useQuery({
    queryKey: LLAVE_FACTURAS,
    queryFn: ({ signal }) => pedir<{ facturas: FacturaCartera[] }>("/api/v1/facturas", { senal: signal }),
    select: (d) => d.facturas,
    retry: false,
  });
}

function trasEscribir(cache: ReturnType<typeof useQueryClient>, llave: readonly string[]) {
  void cache.invalidateQueries({ queryKey: llave });
  void cache.invalidateQueries({ queryKey: LLAVE_LIBRO });
  void cache.invalidateQueries({ queryKey: ["metricas"] });
}

export function usarCrearAbono() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (entrada: Record<string, unknown>) =>
      pedir<{ id: number }>("/api/v1/abonos", { metodo: "POST", cuerpo: entrada }),
    onSuccess: () => trasEscribir(cache, LLAVE_ABONOS),
  });
}

export function usarCrearFactura() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (entrada: Record<string, unknown>) =>
      pedir<{ id: number; creada: boolean }>("/api/v1/facturas", { metodo: "POST", cuerpo: entrada }),
    onSuccess: () => trasEscribir(cache, LLAVE_FACTURAS),
  });
}

export function usarBorrarAbono() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      pedir<{ borrado: boolean }>("/api/v1/abonos/borrar", { metodo: "POST", cuerpo: { id } }),
    onSuccess: () => trasEscribir(cache, LLAVE_ABONOS),
  });
}

export function usarBorrarFactura() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      pedir<{ borrado: boolean }>("/api/v1/facturas/borrar", { metodo: "POST", cuerpo: { id } }),
    onSuccess: () => trasEscribir(cache, LLAVE_FACTURAS),
  });
}
